import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import makeStyles from '@mui/styles/makeStyles';
import { datePickerFormat, FilterModel, FilterType, SelectFilterModelSimple } from 'features/common/types';
import { formatDateToShowUser, getFilterModalTitle } from 'features/common/helpers';
import { isValidDate } from 'features/quotation/helpers';
import { advancedListFilters, QUICK_FILTER_KEYS } from './constants';

interface ServicesFiltersProps {
	filters: FilterModel[];
	onFiltersChange: (filters: FilterModel[]) => void;
}

const useStyles = makeStyles((theme) => ({
	chipWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
	},
}));

const BOOLEAN_OPTIONS = [
	{ value: '', label: 'Todos' },
	{ value: 'true', label: 'Sí' },
	{ value: 'false', label: 'No' },
];

const parseDateValue = (value?: string) => {
	if (!value) return null;
	const date = new Date(`${value}T00:00:00`);
	return isValidDate(date) ? date : null;
};

const formatDateValue = (dateValue: Date | null) => {
	if (!dateValue || !isValidDate(dateValue)) return '';
	return dateValue.toLocaleString('sv', { timeZoneName: 'short' }).split(' ')[0];
};

const FILTER_DEBOUNCE_MS = 400;

interface DebouncedTextFilterProps {
	filter: FilterModel;
	appliedValue: string;
	onApply: (filter: FilterModel) => void;
	onDelete: (filter: FilterModel) => void;
	inputType?: 'text' | 'number';
}

const DebouncedTextFilter = ({
	filter,
	appliedValue,
	onApply,
	onDelete,
	inputType = 'text',
}: DebouncedTextFilterProps): JSX.Element => {
	const [draft, setDraft] = useState(appliedValue);
	const debouncerRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		setDraft(appliedValue);
	}, [appliedValue]);

	useEffect(
		() => () => {
			if (debouncerRef.current) {
				clearTimeout(debouncerRef.current);
			}
		},
		[],
	);

	const handleChange = (value: string) => {
		setDraft(value);

		if (debouncerRef.current) {
			clearTimeout(debouncerRef.current);
		}

		debouncerRef.current = setTimeout(() => {
			if (!value.trim()) {
				onDelete(filter);
				return;
			}
			onApply({ ...filter, value: value.trim() });
		}, FILTER_DEBOUNCE_MS);
	};

	return (
		<TextField
			fullWidth
			size="small"
			label={filter.label}
			type={inputType}
			value={draft}
			onChange={(event) => handleChange(event.target.value)}
		/>
	);
};

export const ServicesFilters = ({ filters, onFiltersChange }: ServicesFiltersProps): JSX.Element => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState(false);

	const quickFilters = useMemo(
		() => advancedListFilters.filter((filter) => QUICK_FILTER_KEYS.includes(filter.key)),
		[],
	);
	const expandedFilters = useMemo(
		() => advancedListFilters.filter((filter) => !QUICK_FILTER_KEYS.includes(filter.key)),
		[],
	);

	const applyFilters = (next: FilterModel[]) => {
		onFiltersChange(next);
	};

	const handleApplyFilter = (filter: FilterModel) => {
		const next = [...filters];
		const index = next.findIndex((item) => item.key === filter.key);
		if (index !== -1) {
			next[index] = filter;
		} else {
			next.push(filter);
		}
		applyFilters(next);
	};

	const handleDeleteFilter = (filter: FilterModel) => {
		applyFilters(filters.filter((item) => item.key !== filter.key));
	};

	const handleDeleteAllFilters = () => applyFilters([]);

	const getAppliedFilter = (filter: FilterModel): FilterModel => {
		return filters.find((item) => item.key === filter.key) ?? filter;
	};

	const handleBooleanChange = (filter: FilterModel, value: string) => {
		if (!value) {
			handleDeleteFilter(filter);
			return;
		}
		handleApplyFilter({ ...filter, value });
	};

	const handleDateChange = (filter: FilterModel, value: Date | null) => {
		const formatted = formatDateValue(value);
		if (!formatted) {
			handleDeleteFilter(filter);
			return;
		}
		handleApplyFilter({ ...filter, value: formatted });
	};

	const handleListChange = (filter: FilterModel, value: string) => {
		if (!value) {
			handleDeleteFilter(filter);
			return;
		}
		if (filter instanceof SelectFilterModelSimple) {
			handleApplyFilter(new SelectFilterModelSimple(filter.label, filter.key, filter.data, value));
			return;
		}
		handleApplyFilter({ ...filter, value });
	};

	const getChipLabel = (filter: FilterModel): string => {
		if (filter.type === FilterType.BOOLEAN) {
			const label = filter.value === 'true' || filter.value === true ? 'Sí' : 'No';
			return `${filter.label}: ${label}`;
		}
		if (filter.type === FilterType.DATE && typeof filter.value === 'string') {
			return `${filter.label}: ${formatDateToShowUser(filter.value)}`;
		}
		if (filter.type === FilterType.LIST && filter instanceof SelectFilterModelSimple) {
			const name = filter.data.find((item) => String(item.id) === String(filter.value))?.name ?? filter.value;
			return `${filter.label}: ${name}`;
		}
		if (filter.type === FilterType.STRING || filter.type === FilterType.NUMERIC) {
			return `${getFilterModalTitle(filter)} ${filter.value}`;
		}
		return `${filter.label} ${filter.value}`;
	};

	const renderInlineFilter = (filter: FilterModel) => {
		const applied = getAppliedFilter(filter);

		if (filter.type === FilterType.BOOLEAN) {
			return (
				<FormControl fullWidth size="small">
					<InputLabel id={`${filter.key}-label`}>{filter.label}</InputLabel>
					<Select
						labelId={`${filter.key}-label`}
						label={filter.label}
						value={applied.value ?? ''}
						onChange={(event) => handleBooleanChange(filter, event.target.value)}
					>
						{BOOLEAN_OPTIONS.map((option) => (
							<MenuItem key={`${filter.key}-${option.value}`} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		if (filter.type === FilterType.LIST && filter instanceof SelectFilterModelSimple) {
			return (
				<FormControl fullWidth size="small">
					<InputLabel id={`${filter.key}-label`}>{filter.label}</InputLabel>
					<Select
						labelId={`${filter.key}-label`}
						label={filter.label}
						value={applied.value ?? ''}
						onChange={(event) => handleListChange(filter, event.target.value)}
					>
						<MenuItem value="">
							<em>Todos</em>
						</MenuItem>
						{filter.data.map((item) => (
							<MenuItem key={String(item.id)} value={String(item.id)}>
								{item.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		if (filter.type === FilterType.DATE) {
			return (
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={filter.label}
						inputFormat={datePickerFormat}
						value={parseDateValue(applied.value as string)}
						onChange={(value) => handleDateChange(filter, value)}
						renderInput={(params) => <TextField {...params} fullWidth size="small" />}
					/>
				</LocalizationProvider>
			);
		}

		if (filter.type === FilterType.NUMERIC) {
			return (
				<DebouncedTextFilter
					filter={filter}
					appliedValue={applied.value !== undefined && applied.value !== null ? String(applied.value) : ''}
					onApply={handleApplyFilter}
					onDelete={handleDeleteFilter}
					inputType="number"
				/>
			);
		}

		return (
			<DebouncedTextFilter
				filter={filter}
				appliedValue={applied.value !== undefined && applied.value !== null ? String(applied.value) : ''}
				onApply={handleApplyFilter}
				onDelete={handleDeleteFilter}
			/>
		);
	};

	const activeCount = filters.length;

	return (
		<Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Badge badgeContent={activeCount} color="primary">
						<FilterListIcon color="action" />
					</Badge>
					<Typography variant="subtitle1" fontWeight={500} component="h2">
						Filtros
					</Typography>
				</Box>

				<Box display="flex" gap={1}>
					<Tooltip title="Limpiar todos los filtros">
						<span>
							<Button
								size="small"
								startIcon={<ClearIcon />}
								onClick={handleDeleteAllFilters}
								variant="outlined"
								color="error"
								disabled={activeCount === 0}
							>
								Limpiar
							</Button>
						</span>
					</Tooltip>
					{expandedFilters.length > 0 && (
						<Tooltip title={expanded ? 'Ocultar filtros' : 'Mostrar más filtros'}>
							<Button
								size="small"
								endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setExpanded((prev) => !prev)}
								variant="outlined"
								color="primary"
							>
								{expanded ? 'Menos filtros' : 'Más filtros'}
							</Button>
						</Tooltip>
					)}
				</Box>
			</Box>

			{activeCount > 0 && (
				<Box className={classes.chipWrap} mb={1.5}>
					{filters.map((filter) => (
						<Chip
							key={`chip-${filter.key}`}
							label={getChipLabel(filter)}
							onDelete={() => handleDeleteFilter(filter)}
							color="primary"
							variant="outlined"
							size="small"
						/>
					))}
				</Box>
			)}

			<Grid container spacing={1.5}>
				{quickFilters.map((filter) => (
					<Grid item key={`quick-${filter.key}`} xs={12} sm={6} md={3}>
						{renderInlineFilter(filter)}
					</Grid>
				))}
			</Grid>

			{expandedFilters.length > 0 && (
				<Collapse in={expanded}>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={1.5}>
						{expandedFilters.map((filter) => (
							<Grid item key={`expanded-${filter.key}`} xs={12} sm={6} md={4}>
								{renderInlineFilter(filter)}
							</Grid>
						))}
					</Grid>
				</Collapse>
			)}
		</Paper>
	);
};
